import { useQuery } from '@tanstack/react-query';
import { CreditCard, Wallet } from 'lucide-react';
import { PERMISSIONS, type EmployeeLoan, type EmployeeAdvance } from '@pms/shared';
import { payrollService } from '@/services/payroll.service';
import { usePermission } from '@/hooks/usePermission';
import { Card, CardContent } from '@/components/ui/card';
import { SectionHeader } from '@/components/ui/section-header';
import { LOAN_STATUS_COLORS, ADVANCE_STATUS_COLORS } from '@/components/payroll/loan-status';

function formatCurrency(val: string | number): string {
  const n = typeof val === 'string' ? parseFloat(val) : val;
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

export function StaffLoansTab({ userId }: { userId: string }) {
  const perms = usePermission();
  const canView = perms.has(PERMISSIONS.LOAN_MANAGE);

  const { data: loansData, isLoading: loansLoading } = useQuery({
    queryKey: ['payroll', 'loans', { user_id: userId }],
    queryFn: () => payrollService.listLoans({ user_id: userId, limit: 20 }),
    enabled: canView,
  });

  const { data: advData, isLoading: advLoading } = useQuery({
    queryKey: ['payroll', 'advances', { user_id: userId }],
    queryFn: () => payrollService.listAdvances({ user_id: userId, limit: 20 }),
    enabled: canView,
  });

  if (!canView) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-sm text-muted-foreground">
          You don't have permission to view this employee's loans.
        </CardContent>
      </Card>
    );
  }

  const loans = loansData?.data ?? [];
  const advances = advData?.data ?? [];

  return (
    <div className="space-y-4">
      {/* Loans */}
      <Card>
        <SectionHeader icon={<CreditCard />} title="Employee Loans" />
        <CardContent className="p-4">
          {loansLoading ? (
            <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>
          ) : loans.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">No loans found</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/60 text-left text-xs font-medium text-muted-foreground">
                    <th className="pb-2 pr-4">Amount</th>
                    <th className="pb-2 pr-4">EMI</th>
                    <th className="pb-2 pr-4">EMIs Paid</th>
                    <th className="pb-2 pr-4">Remaining</th>
                    <th className="pb-2 pr-4">Interest</th>
                    <th className="pb-2 pr-4">Start</th>
                    <th className="pb-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loans.map((loan: EmployeeLoan) => {
                    const emisPaid = loan.emis_paid ?? 0;
                    const remaining = loan.remaining_amount
                      ?? String(parseFloat(loan.amount) - emisPaid * parseFloat(loan.emi_amount));
                    return (
                      <tr key={loan.id} className="border-b border-border/40 last:border-0">
                        <td className="py-2 pr-4 font-medium">{formatCurrency(loan.amount)}</td>
                        <td className="py-2 pr-4">{formatCurrency(loan.emi_amount)}</td>
                        <td className="py-2 pr-4">{emisPaid}/{loan.num_emis}</td>
                        <td className="py-2 pr-4">{formatCurrency(Math.max(0, parseFloat(remaining)))}</td>
                        <td className="py-2 pr-4">{parseFloat(loan.interest_rate).toFixed(1)}%</td>
                        <td className="py-2 pr-4 text-xs text-muted-foreground">{loan.start_date}</td>
                        <td className="py-2">
                          <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${LOAN_STATUS_COLORS[loan.status] ?? ''}`}>
                            {loan.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Advances */}
      <Card>
        <SectionHeader icon={<Wallet />} title="Salary Advances" />
        <CardContent className="p-4">
          {advLoading ? (
            <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>
          ) : advances.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">No advances found</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/60 text-left text-xs font-medium text-muted-foreground">
                    <th className="pb-2 pr-4">Amount</th>
                    <th className="pb-2 pr-4">Advance Date</th>
                    <th className="pb-2 pr-4">Deduction Month</th>
                    <th className="pb-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {advances.map((adv: EmployeeAdvance) => (
                    <tr key={adv.id} className="border-b border-border/40 last:border-0">
                      <td className="py-2 pr-4 font-medium">{formatCurrency(adv.amount)}</td>
                      <td className="py-2 pr-4 text-xs text-muted-foreground">{adv.advance_date}</td>
                      <td className="py-2 pr-4 text-xs text-muted-foreground">
                        {adv.deduction_year && adv.deduction_month
                          ? new Date(adv.deduction_year, adv.deduction_month - 1)
                              .toLocaleString('en-IN', { month: 'short', year: 'numeric' })
                          : '—'}
                      </td>
                      <td className="py-2">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${ADVANCE_STATUS_COLORS[adv.status] ?? ''}`}>
                          {adv.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
